import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const STORAGE_KEY = 'allwhitepouch-age-18';
const DATE_KEY = 'allwhitepouch-age-18-date';

/** In-memory fallback when sessionStorage is blocked. */
let memoryConfirmed = false;
let memoryDate = '';

/**
 * @returns {boolean}
 */
function readConfirmed() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return memoryConfirmed;
  }
}

/**
 * @returns {{ year: string, month: string, day: string } | null}
 */
function readDate() {
  let value = memoryDate;
  try {
    value = sessionStorage.getItem(DATE_KEY) || '';
  } catch {
    value = memoryDate;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return { year: match[1], month: match[2], day: match[3] };
}

/**
 * @param {string} iso
 */
function writeDate(iso) {
  memoryDate = iso;
  try {
    sessionStorage.setItem(DATE_KEY, iso);
  } catch {
    // Private browsing can throw. The in-memory date still fills this page.
  }
}

/**
 * @param {boolean} confirmed
 */
function writeConfirmed(confirmed) {
  memoryConfirmed = confirmed;
  if (!confirmed) memoryDate = '';
  try {
    if (confirmed) sessionStorage.setItem(STORAGE_KEY, '1');
    else {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(DATE_KEY);
    }
  } catch {
    // Private browsing can throw. The in-memory flag still gates this page.
  }
  syncAll();
}

/** Push the saved verification onto every cart surface (drawer and cart page). */
function syncAll() {
  const confirmed = readConfirmed();
  for (const node of document.querySelectorAll('cart-age-confirm-component')) {
    if (node instanceof CartAgeConfirm) node.applyConfirmed(confirmed);
  }
}

/**
 * @param {string} day
 * @param {string} month
 * @param {string} year
 * @returns {Date | null}
 */
function parseDob(day, month, year) {
  if (!/^\d{1,2}$/.test(day) || !/^\d{1,2}$/.test(month) || !/^\d{4}$/.test(year)) return null;
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (y < 1900 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date > today) return null;
  return date;
}

/**
 * @param {Date} date
 * @returns {number}
 */
function ageInYears(date) {
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDelta = today.getMonth() - date.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < date.getDate())) age -= 1;
  return age;
}

/**
 * Date-of-birth gate for the cart drawer and cart page.
 * Checkout and express wallets stay blocked until Submit age confirms the shopper is 18+.
 *
 * @typedef {object} CartAgeConfirmRefs
 * @property {HTMLInputElement} day
 * @property {HTMLInputElement} month
 * @property {HTMLInputElement} year
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} submitLabel
 * @property {HTMLElement} spinner
 * @property {HTMLButtonElement} checkout
 * @property {HTMLElement} [express]
 * @property {HTMLElement} message
 * @property {HTMLButtonElement} shield
 *
 * @extends {Component<CartAgeConfirmRefs>}
 */
class CartAgeConfirm extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  /** Ignores input events caused by filling the saved date. */
  #syncing = false;

  /** True while the age check loader is showing. */
  #busy = false;

  /** Cancels an in-flight age check when the date changes. */
  #requestId = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.addEventListener('click', this.#intercept, { capture: true, signal });
    this.addEventListener('pointerdown', this.#intercept, { capture: true, signal });

    for (const field of [this.refs.day, this.refs.month, this.refs.year]) {
      if (!(field instanceof HTMLInputElement)) continue;
      field.addEventListener('input', this.#onFieldInput, { signal });
      field.addEventListener('keydown', this.#onFieldKeydown, { signal });
    }
    this.refs.submit?.addEventListener('click', this.#verify, { signal });
    this.applyConfirmed(readConfirmed());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  updatedCallback() {
    super.updatedCallback();
    this.applyConfirmed(readConfirmed());
  }

  /**
   * Apply the shared age verification to this cart surface.
   * @param {boolean} confirmed
   */
  applyConfirmed(confirmed) {
    this.#apply(confirmed);
  }

  prompt() {
    this.#showError('required');
  }

  /**
   * @param {Event} event
   */
  #onFieldInput = (event) => {
    if (this.#syncing) return;
    const field = event.currentTarget;
    if (!(field instanceof HTMLInputElement)) return;
    const max = Number(field.getAttribute('maxlength')) || field.value.length;
    field.value = field.value.replace(/\D/g, '').slice(0, max);
    this.#requestId += 1;
    this.#busy = false;
    if (readConfirmed() && !this.#matchesStored()) writeConfirmed(false);
    else if (!readConfirmed()) this.#setButton('submit');
    if (field.value.length >= max) {
      const fields = [this.refs.day, this.refs.month, this.refs.year];
      const next = fields[fields.indexOf(field) + 1];
      if (next instanceof HTMLInputElement) next.focus();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onFieldKeydown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.#verify();
  };

  #verify = () => {
    if (this.#busy || readConfirmed()) return;
    const day = this.refs.day instanceof HTMLInputElement ? this.refs.day.value.trim() : '';
    const month = this.refs.month instanceof HTMLInputElement ? this.refs.month.value.trim() : '';
    const year = this.refs.year instanceof HTMLInputElement ? this.refs.year.value.trim() : '';

    if (!day || !month || !year) {
      this.#showError('required');
      return;
    }

    const date = parseDob(day, month, year);
    if (!date) {
      this.#showError('invalid');
      return;
    }

    const requestId = ++this.#requestId;
    this.#busy = true;
    this.#setButton('loading');

    window.setTimeout(() => {
      if (requestId !== this.#requestId || !this.isConnected) return;
      this.#busy = false;

      if (ageInYears(date) < 18) {
        this.#setButton('submit');
        this.#showError('underage');
        return;
      }

      writeDate(
        [
          String(date.getFullYear()),
          String(date.getMonth() + 1).padStart(2, '0'),
          String(date.getDate()).padStart(2, '0'),
        ].join('-')
      );
      writeConfirmed(true);
    }, 700);
  };

  /**
   * @param {'required' | 'invalid' | 'underage'} kind
   */
  #showError(kind) {
    const message = this.refs.message;
    if (message instanceof HTMLElement) {
      message.textContent = message.dataset[kind] || message.dataset.invalid || '';
      message.hidden = false;
    }
    this.classList.add('is-invalid');
    const fields = [this.refs.day, this.refs.month, this.refs.year];
    for (const field of fields) {
      if (field instanceof HTMLInputElement) field.setAttribute('aria-invalid', 'true');
    }
    const empty = fields.find((field) => field instanceof HTMLInputElement && field.value.trim() === '');
    const focusTarget = empty instanceof HTMLInputElement ? empty : fields[0];
    if (focusTarget instanceof HTMLInputElement) focusTarget.focus();
  }

  /**
   * @param {boolean} confirmed
   */
  /**
   * @returns {boolean}
   */
  #matchesStored() {
    const stored = readDate();
    if (!stored) return false;
    const day = this.refs.day instanceof HTMLInputElement ? this.refs.day.value : '';
    const month = this.refs.month instanceof HTMLInputElement ? this.refs.month.value : '';
    const year = this.refs.year instanceof HTMLInputElement ? this.refs.year.value : '';
    return day === stored.day && month === stored.month && year === stored.year;
  }

  /**
   * @param {'submit' | 'loading' | 'verified'} mode
   */
  #setButton(mode) {
    const button = this.refs.submit;
    const label = this.refs.submitLabel;
    const spinner = this.refs.spinner;
    if (!(button instanceof HTMLButtonElement)) return;

    const text =
      mode === 'verified'
        ? button.dataset.labelVerified
        : mode === 'loading'
          ? button.dataset.labelChecking
          : button.dataset.labelSubmit;

    if (label instanceof HTMLElement && text) label.textContent = text;
    if (spinner instanceof HTMLElement) spinner.hidden = mode !== 'loading';
    button.classList.toggle('is-loading', mode === 'loading');
    button.disabled = mode !== 'submit';
    if (mode === 'loading') button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  #apply(confirmed) {
    if (this.#busy) return;

    const checkout = this.refs.checkout;
    const express = this.refs.express;
    const stored = readDate();
    const verified = confirmed && stored != null;

    if (verified && stored) {
      this.#syncing = true;
      if (this.refs.day instanceof HTMLInputElement) this.refs.day.value = stored.day;
      if (this.refs.month instanceof HTMLInputElement) this.refs.month.value = stored.month;
      if (this.refs.year instanceof HTMLInputElement) this.refs.year.value = stored.year;
      this.#syncing = false;
    }

    const cartEmpty = checkout instanceof HTMLButtonElement && checkout.hasAttribute('data-cart-empty');
    if (checkout instanceof HTMLButtonElement) {
      const locked = cartEmpty || !verified;
      checkout.disabled = locked;
      checkout.setAttribute('aria-disabled', locked ? 'true' : 'false');
    }

    if (express instanceof HTMLElement) {
      if (verified) express.removeAttribute('inert');
      else express.setAttribute('inert', '');
    }

    this.classList.toggle('is-confirmed', verified);
    this.#setButton(verified ? 'verified' : 'submit');

    if (verified) {
      if (this.refs.message instanceof HTMLElement) this.refs.message.hidden = true;
      this.classList.remove('is-invalid');
      for (const field of [this.refs.day, this.refs.month, this.refs.year]) {
        if (field instanceof HTMLInputElement) field.removeAttribute('aria-invalid');
      }
    }
  }

  /**
   * Block checkout and wallet buttons until the date of birth is accepted.
   * @param {Event} event
   */
  #intercept = (event) => {
    if (readConfirmed() || !this.#hitsPurchaseControl(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'pointerdown') this.prompt();
  };

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #hitsPurchaseControl(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (const node of path) {
      if (!(node instanceof Element)) continue;
      if (
        node.classList.contains('cart-age-confirm__field') ||
        node.classList.contains('cart-age-confirm__fields') ||
        node.classList.contains('cart-age-confirm__submit')
      ) {
        return false;
      }
      if (
        node.classList.contains('cart__ctas') ||
        node.classList.contains('cart__checkout-button') ||
        node.classList.contains('additional-checkout-buttons') ||
        node.classList.contains('cart-age-confirm__shield') ||
        node.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT' ||
        node.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART'
      ) {
        return true;
      }
    }
    return false;
  }
}

if (!customElements.get('cart-age-confirm-component')) {
  customElements.define('cart-age-confirm-component', CartAgeConfirm);
}

window.addEventListener('pageshow', () => syncAll());
document.addEventListener('theme-drawer:open', (event) => {
  const drawer = event.target;
  if (!(drawer instanceof Element) || drawer.id !== 'cart-drawer') return;
  syncAll();
});
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const schedule = () => {
    setTimeout(syncAll, 0);
  };
  const promise = 'promise' in event ? event.promise : null;
  if (promise && typeof promise.then === 'function') promise.then(schedule, schedule);
  else schedule();
});

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('cart-form')) return;

    const submitter = event.submitter;
    if (!(submitter instanceof HTMLElement)) return;
    const isCheckout = submitter.getAttribute('name') === 'checkout' || submitter.id === 'checkout';
    if (!isCheckout || readConfirmed()) return;

    event.preventDefault();
    event.stopPropagation();
    const gate = document.querySelector('cart-age-confirm-component');
    if (gate instanceof CartAgeConfirm) gate.prompt();
  },
  true
);
